import {
  addBusinessDays,
  maxDate,
  parseGanttDate,
  toStartOfUtcDay,
} from './gantt-date-utils';
import { GANTT_DEFAULT_SECTION_LABEL } from './gantt-constants';
import {
  GanttDateFormat,
  GanttTaskStatus,
  type GanttTask,
  type GanttSection,
  type ParsedGantt,
} from './gantt-types';

interface TaskDraft {
  id: string;
  label: string;
  status: GanttTaskStatus;
  sectionIndex: number;
  dependsOn: string[];
  explicitStartDate: Date | null;
  explicitEndDate: Date | null;
  durationDays: number | null;
  usesAfterDependency: boolean;
}

interface TaskDraftParseInput {
  line: string;
  sectionIndex: number;
  generatedId: string;
  dateFormat: string;
  excludesWeekends: boolean;
}

interface TaskLineParts {
  label: string;
  metadataExpression: string;
}

interface TaskLineTokenSelection {
  label: string;
  tokens: string[];
}

interface TaskStatusSelection {
  status: GanttTaskStatus;
  nextTokenIndex: number;
}

interface TaskIdentitySelection {
  taskId: string;
  nextTokenIndex: number;
}

interface TaskScheduleDateSelection {
  explicitStartDate: Date | null;
  explicitEndDate: Date | null;
  durationDays: number | null;
}

interface TaskScheduleSelection extends TaskScheduleDateSelection {
  dependsOn: string[];
  usesAfterDependency: boolean;
}

interface GanttParseState {
  title: string | null;
  dateFormat: string;
  excludesWeekends: boolean;
  sections: GanttSection[];
  taskDrafts: TaskDraft[];
  currentSectionIndex: number;
  generatedTaskIndex: number;
}

interface GanttLineInstruction {
  kind: GanttLineKind;
  value: string | null;
}

interface GanttLineReducerInput {
  state: GanttParseState;
  instruction: GanttLineInstruction;
}

type GanttLineInstructionParser = (line: string) => GanttLineInstruction | null;
type GanttLineReducer = (input: GanttLineReducerInput) => GanttParseState;

enum DurationUnit {
  Day = 'd',
  Week = 'w',
}

enum GanttLineKind {
  DateFormat = 'dateFormat',
  ExcludesWeekends = 'excludesWeekends',
  Ignored = 'ignored',
  Section = 'section',
  Task = 'task',
  Title = 'title',
}

const GANTT_HEADER_REGEX = /^gantt\b/i;
const TITLE_REGEX = /^title\s+(.+)$/i;
const DATE_FORMAT_REGEX = /^dateFormat\s+(.+)$/i;
const SECTION_REGEX = /^section\s+(.+)$/i;
const EXCLUDES_WEEKENDS_REGEX = /^excludes\s+weekends?$/i;
const AFTER_DEPENDENCY_REGEX = /^after\s+/i;
const DURATION_TOKEN_REGEX = /^(\d+)\s*([dw])$/i;
const LABEL_WHITESPACE_REGEX = /\s+/g;
const DEPENDENCY_WHITESPACE_REGEX = /\s+/;
const LINE_TERMINATOR_REGEX = /;\s*$/;
const TRAILING_WHITESPACE_REGEX = /\s+$/;
const MERMAID_COMMENT_MARKER = '%%';
const MERMAID_LINE_SEPARATOR = '\n';
const GENERATED_TASK_ID_PREFIX = 'task';
const NORMALIZED_LABEL_SEPARATOR = ' ';
const TASK_METADATA_SEPARATOR = ':';
const TASK_METADATA_TOKEN_SEPARATOR = ',';

const DURATION_UNIT_DAY_COUNT: Readonly<Record<DurationUnit, number>> = {
  [DurationUnit.Day]: 1,
  [DurationUnit.Week]: 7,
};

const DURATION_UNITS: ReadonlySet<string> = new Set(Object.values(DurationUnit));

const STATUS_BY_TOKEN: Readonly<Record<string, GanttTaskStatus>> = {
  [GanttTaskStatus.Done]: GanttTaskStatus.Done,
  [GanttTaskStatus.Active]: GanttTaskStatus.Active,
  [GanttTaskStatus.Crit]: GanttTaskStatus.Crit,
  [GanttTaskStatus.Milestone]: GanttTaskStatus.Milestone,
};

const IGNORED_GANTT_LINE_INSTRUCTION: GanttLineInstruction = {
  kind: GanttLineKind.Ignored,
  value: null,
};

function sanitizeMermaidLine(line: string): string {
  const commentIndex = line.indexOf(MERMAID_COMMENT_MARKER);
  const withoutComment = commentIndex >= 0 ? line.slice(0, commentIndex) : line;
  return withoutComment.replace(LINE_TERMINATOR_REGEX, '').replace(TRAILING_WHITESPACE_REGEX, '');
}

function normalizeLabel(rawLabel: string): string {
  return rawLabel.replace(LABEL_WHITESPACE_REGEX, NORMALIZED_LABEL_SEPARATOR).trim();
}

function isDurationUnit(unit: string | undefined): unit is DurationUnit {
  return !!unit && DURATION_UNITS.has(unit);
}

function isValidDurationAmount(amount: number): boolean {
  return Number.isFinite(amount) && amount >= 0;
}

function parseDurationToken(token: string): number | null {
  const normalized = token.trim().toLowerCase();
  const match = normalized.match(DURATION_TOKEN_REGEX);
  if (!match) {
    return null;
  }

  const amount = Number(match[1]);
  const unit = match[2]?.toLowerCase();
  if (!isValidDurationAmount(amount) || !isDurationUnit(unit)) {
    return null;
  }

  return amount * DURATION_UNIT_DAY_COUNT[unit];
}

function parseAfterToken(token: string): string[] {
  const normalized = token.trim();
  if (!AFTER_DEPENDENCY_REGEX.test(normalized)) {
    return [];
  }

  const dependencyExpression = normalized.replace(AFTER_DEPENDENCY_REGEX, '').trim();
  if (!dependencyExpression) {
    return [];
  }

  return dependencyExpression
    .split(DEPENDENCY_WHITESPACE_REGEX)
    .map((dependencyId) => dependencyId.trim())
    .filter((dependencyId) => dependencyId.length > 0);
}

function parseTaskLineParts(line: string): TaskLineParts | null {
  const separatorIndex = line.indexOf(TASK_METADATA_SEPARATOR);
  if (separatorIndex <= 0) {
    return null;
  }

  const rawLabel = line.slice(0, separatorIndex);
  const metadataExpression = line.slice(separatorIndex + TASK_METADATA_SEPARATOR.length);
  const label = normalizeLabel(rawLabel);
  if (!label) {
    return null;
  }

  return {
    label,
    metadataExpression,
  };
}

function parseMetadataTokens(input: { metadataExpression: string; dateFormat: string }): string[] {
  const parts = input.metadataExpression
    .split(TASK_METADATA_TOKEN_SEPARATOR)
    .map((token) => token.trim())
    .filter((token) => token.length > 0);
  const tokens: string[] = [];
  for (let index = 0; index < parts.length; index += 1) {
    let end = index;
    for (let candidateEnd = index + 1; candidateEnd < parts.length; candidateEnd += 1) {
      const candidate = parts.slice(index, candidateEnd + 1).join(', ');
      if (parseGanttDate(candidate, input.dateFormat) !== null) end = candidateEnd;
    }
    tokens.push(parts.slice(index, end + 1).join(', '));
    index = end;
  }
  return tokens;
}

function parseTaskLineTokens(input: { line: string; dateFormat: string }): TaskLineTokenSelection | null {
  const taskLineParts = parseTaskLineParts(input.line);
  if (!taskLineParts) {
    return null;
  }

  const tokens = parseMetadataTokens({ metadataExpression: taskLineParts.metadataExpression, dateFormat: input.dateFormat });
  if (tokens.length === 0) {
    return null;
  }

  return {
    label: taskLineParts.label,
    tokens,
  };
}

function getTaskStatus(token: string): GanttTaskStatus | null {
  return STATUS_BY_TOKEN[token.toLowerCase()] ?? null;
}

function getFirstNonStatusTokenIndex(tokens: string[]): number {
  const firstNonStatusTokenIndex = tokens.findIndex((token) => !getTaskStatus(token));
  return firstNonStatusTokenIndex < 0 ? tokens.length : firstNonStatusTokenIndex;
}

function resolveStatusTokens(statusTokens: string[]): GanttTaskStatus {
  return statusTokens.reduce<GanttTaskStatus>(
    (selectedStatus, token) => getTaskStatus(token) ?? selectedStatus,
    GanttTaskStatus.None,
  );
}

function selectTaskStatus(input: {
  tokens: string[];
}): TaskStatusSelection {
  const nextTokenIndex = getFirstNonStatusTokenIndex(input.tokens);
  return {
    status: resolveStatusTokens(input.tokens.slice(0, nextTokenIndex)),
    nextTokenIndex,
  };
}

function isScheduleLikeToken(input: {
  token: string;
  dateFormat: string;
}): boolean {
  return AFTER_DEPENDENCY_REGEX.test(input.token)
    || parseGanttDate(input.token, input.dateFormat) != null
    || parseDurationToken(input.token) != null;
}

function selectTaskIdentity(input: {
  tokens: string[];
  tokenIndex: number;
  generatedId: string;
  dateFormat: string;
}): TaskIdentitySelection {
  const idCandidate = input.tokens[input.tokenIndex];
  if (!idCandidate || isScheduleLikeToken({ token: idCandidate, dateFormat: input.dateFormat })) {
    return {
      taskId: input.generatedId,
      nextTokenIndex: input.tokenIndex,
    };
  }

  return {
    taskId: idCandidate,
    nextTokenIndex: input.tokenIndex + 1,
  };
}

function createEmptyScheduleDateSelection(): TaskScheduleDateSelection {
  return {
    explicitStartDate: null,
    explicitEndDate: null,
    durationDays: null,
  };
}

function parseAfterScheduleFollowup(input: {
  token: string | undefined;
  dateFormat: string;
}): TaskScheduleDateSelection {
  if (!input.token) {
    return createEmptyScheduleDateSelection();
  }

  const durationDays = parseDurationToken(input.token);
  if (durationDays != null) {
    return {
      explicitStartDate: null,
      explicitEndDate: null,
      durationDays,
    };
  }

  return {
    explicitStartDate: parseGanttDate(input.token, input.dateFormat),
    explicitEndDate: null,
    durationDays: null,
  };
}

function parseAfterDependencySchedule(input: {
  scheduleToken: string;
  scheduleTokenNext: string | undefined;
  dateFormat: string;
}): TaskScheduleSelection | null {
  const dependsOn = parseAfterToken(input.scheduleToken);
  if (dependsOn.length === 0) {
    return null;
  }

  return {
    ...parseAfterScheduleFollowup({
      token: input.scheduleTokenNext,
      dateFormat: input.dateFormat,
    }),
    dependsOn,
    usesAfterDependency: true,
  };
}

function parseAbsoluteScheduleEnd(input: {
  startDate: Date;
  token: string | undefined;
  dateFormat: string;
  excludesWeekends: boolean;
}): TaskScheduleDateSelection {
  const token = input.token;
  if (!token) {
    return {
      ...createEmptyScheduleDateSelection(),
      explicitStartDate: input.startDate,
    };
  }

  return {
    explicitStartDate: input.startDate,
    ...parseAbsoluteScheduleEndToken({
      startDate: input.startDate,
      token,
      dateFormat: input.dateFormat,
      excludesWeekends: input.excludesWeekends,
    }),
  };
}

function parseAbsoluteScheduleEndToken(input: {
  startDate: Date;
  token: string;
  dateFormat: string;
  excludesWeekends: boolean;
}): Omit<TaskScheduleDateSelection, 'explicitStartDate'> {
  const explicitEndDate = parseGanttDate(input.token, input.dateFormat);
  if (explicitEndDate) {
    return {
      explicitEndDate,
      durationDays: null,
    };
  }

  const durationDays = parseDurationToken(input.token);
  if (durationDays == null) {
    return {
      explicitEndDate: null,
      durationDays: null,
    };
  }

  return {
    explicitEndDate: addBusinessDays(input.startDate, durationDays, input.excludesWeekends),
    durationDays,
  };
}

function parseAbsoluteDateSchedule(input: {
  scheduleToken: string;
  scheduleTokenNext: string | undefined;
  dateFormat: string;
  excludesWeekends: boolean;
}): TaskScheduleSelection | null {
  const explicitStartDate = parseGanttDate(input.scheduleToken, input.dateFormat);
  if (!explicitStartDate) {
    return null;
  }

  return {
    ...parseAbsoluteScheduleEnd({
      startDate: explicitStartDate,
      token: input.scheduleTokenNext,
      dateFormat: input.dateFormat,
      excludesWeekends: input.excludesWeekends,
    }),
    dependsOn: [],
    usesAfterDependency: false,
  };
}

function parseTaskSchedule(input: {
  scheduleToken: string;
  scheduleTokenNext: string | undefined;
  dateFormat: string;
  excludesWeekends: boolean;
}): TaskScheduleSelection | null {
  const afterDependencySchedule = parseAfterDependencySchedule(input);
  if (afterDependencySchedule) {
    return afterDependencySchedule;
  }

  return parseAbsoluteDateSchedule(input);
}

function parseTaskScheduleFromTokens(input: {
  tokens: string[];
  tokenIndex: number;
  dateFormat: string;
  excludesWeekends: boolean;
}): TaskScheduleSelection | null {
  const scheduleToken = input.tokens[input.tokenIndex];
  if (!scheduleToken) {
    return null;
  }

  return parseTaskSchedule({
    scheduleToken,
    scheduleTokenNext: input.tokens[input.tokenIndex + 1],
    dateFormat: input.dateFormat,
    excludesWeekends: input.excludesWeekends,
  });
}

function applyMilestoneSchedule(input: {
  status: GanttTaskStatus;
  schedule: TaskScheduleSelection;
}): TaskScheduleSelection {
  if (input.status !== GanttTaskStatus.Milestone) {
    return input.schedule;
  }

  if (input.schedule.explicitStartDate) {
    return {
      ...input.schedule,
      explicitEndDate: input.schedule.explicitStartDate,
    };
  }

  if (input.schedule.durationDays != null) {
    return input.schedule;
  }

  return {
    ...input.schedule,
    durationDays: 0,
  };
}

function parseTaskDraft(input: TaskDraftParseInput): TaskDraft | null {
  const lineTokens = parseTaskLineTokens({ line: input.line, dateFormat: input.dateFormat });
  if (!lineTokens) {
    return null;
  }

  const statusSelection = selectTaskStatus({ tokens: lineTokens.tokens });
  const identitySelection = selectTaskIdentity({
    tokens: lineTokens.tokens,
    tokenIndex: statusSelection.nextTokenIndex,
    generatedId: input.generatedId,
    dateFormat: input.dateFormat,
  });
  const schedule = parseTaskScheduleFromTokens({
    tokens: lineTokens.tokens,
    tokenIndex: identitySelection.nextTokenIndex,
    dateFormat: input.dateFormat,
    excludesWeekends: input.excludesWeekends,
  });
  if (!schedule) {
    return null;
  }

  const milestoneSchedule = applyMilestoneSchedule({
    status: statusSelection.status,
    schedule,
  });

  return {
    id: identitySelection.taskId,
    label: lineTokens.label,
    status: statusSelection.status,
    sectionIndex: input.sectionIndex,
    dependsOn: milestoneSchedule.dependsOn,
    explicitStartDate: milestoneSchedule.explicitStartDate,
    explicitEndDate: milestoneSchedule.explicitEndDate,
    durationDays: milestoneSchedule.durationDays,
    usesAfterDependency: milestoneSchedule.usesAfterDependency,
  };
}

function selectLatestDependencyEndDate(input: {
  latestEndDate: Date | null;
  dependencyTask: GanttTask | undefined;
}): Date | null {
  if (!input.dependencyTask) {
    return input.latestEndDate;
  }

  if (!input.latestEndDate) {
    return input.dependencyTask.endDate;
  }

  return maxDate(input.latestEndDate, input.dependencyTask.endDate);
}

function resolveLatestDependencyEndDate(input: {
  draft: TaskDraft;
  tasksById: Map<string, GanttTask>;
}): Date | null {
  return input.draft.dependsOn.reduce<Date | null>((latestEndDate, dependencyId) => (
    selectLatestDependencyEndDate({
      latestEndDate,
      dependencyTask: input.tasksById.get(dependencyId),
    })
  ), null);
}

function resolveDraftStartDate(input: {
  draft: TaskDraft;
  latestDependencyEndDate: Date | null;
}): Date | null {
  if (input.draft.explicitStartDate) {
    return input.draft.explicitStartDate;
  }

  if (input.draft.usesAfterDependency) {
    return input.latestDependencyEndDate;
  }

  return null;
}

function resolveDraftEndDate(input: {
  draft: TaskDraft;
  startDate: Date;
  excludesWeekends: boolean;
}): Date {
  if (input.draft.explicitEndDate) {
    return input.draft.explicitEndDate;
  }

  const duration = input.draft.durationDays ?? 0;
  return addBusinessDays(input.startDate, duration, input.excludesWeekends);
}

function isEndDateBeforeStartDate(input: {
  startDate: Date;
  endDate: Date;
}): boolean {
  return toStartOfUtcDay(input.endDate).getTime() < toStartOfUtcDay(input.startDate).getTime();
}

function createGanttTask(input: {
  draft: TaskDraft;
  startDate: Date;
  endDate: Date;
}): GanttTask {
  const normalizedStartDate = toStartOfUtcDay(input.startDate);
  const normalizedEndDate = input.draft.status === GanttTaskStatus.Milestone
    ? normalizedStartDate
    : toStartOfUtcDay(input.endDate);

  return {
    id: input.draft.id,
    label: input.draft.label,
    status: input.draft.status,
    startDate: normalizedStartDate,
    endDate: normalizedEndDate,
    dependsOn: input.draft.dependsOn,
    sectionIndex: input.draft.sectionIndex,
  };
}

function resolveTaskDates(input: {
  drafts: TaskDraft[];
  excludesWeekends: boolean;
}): GanttTask[] | null {
  const tasks: GanttTask[] = [];
  const tasksById = new Map<string, GanttTask>();

  for (const draft of input.drafts) {
    const latestDependencyEndDate = resolveLatestDependencyEndDate({ draft, tasksById });
    const startDate = resolveDraftStartDate({ draft, latestDependencyEndDate });
    if (!startDate) {
      return null;
    }

    const endDate = resolveDraftEndDate({
      draft,
      startDate,
      excludesWeekends: input.excludesWeekends,
    });
    if (isEndDateBeforeStartDate({ startDate, endDate })) {
      return null;
    }

    const task = createGanttTask({ draft, startDate, endDate });
    tasks.push(task);
    tasksById.set(task.id, task);
  }

  return tasks;
}

function parseMermaidLines(raw: string): string[] {
  return raw
    .split(MERMAID_LINE_SEPARATOR)
    .map((line) => sanitizeMermaidLine(line))
    .filter((line) => line.trim().length > 0);
}

function findGanttHeaderIndex(lines: string[]): number {
  return lines.findIndex((line) => GANTT_HEADER_REGEX.test(line.trim()));
}

function parseDirectiveValue(input: {
  line: string;
  regex: RegExp;
}): string | null {
  const match = input.line.match(input.regex);
  if (!match) {
    return null;
  }

  return match[1] ?? '';
}

function parseTitleInstruction(line: string): GanttLineInstruction | null {
  const title = parseDirectiveValue({ line, regex: TITLE_REGEX });
  if (title == null) {
    return null;
  }

  return {
    kind: GanttLineKind.Title,
    value: normalizeLabel(title),
  };
}

function parseDateFormatInstruction(line: string): GanttLineInstruction | null {
  const dateFormat = parseDirectiveValue({ line, regex: DATE_FORMAT_REGEX });
  if (dateFormat == null) {
    return null;
  }

  return {
    kind: GanttLineKind.DateFormat,
    value: dateFormat.trim(),
  };
}

function parseExcludesWeekendsInstruction(line: string): GanttLineInstruction | null {
  if (!EXCLUDES_WEEKENDS_REGEX.test(line)) {
    return null;
  }

  return {
    kind: GanttLineKind.ExcludesWeekends,
    value: null,
  };
}

function parseSectionInstruction(line: string): GanttLineInstruction | null {
  const rawSectionLabel = parseDirectiveValue({ line, regex: SECTION_REGEX });
  if (rawSectionLabel == null) {
    return null;
  }

  const sectionLabel = normalizeLabel(rawSectionLabel);
  if (!sectionLabel) {
    return null;
  }

  return {
    kind: GanttLineKind.Section,
    value: sectionLabel,
  };
}

const GANTT_DIRECTIVE_INSTRUCTION_PARSERS: readonly GanttLineInstructionParser[] = [
  parseTitleInstruction,
  parseDateFormatInstruction,
  parseExcludesWeekendsInstruction,
  parseSectionInstruction,
];

function isGanttLineInstruction(
  instruction: GanttLineInstruction | null,
): instruction is GanttLineInstruction {
  return instruction != null;
}

function parseGanttLine(line: string): GanttLineInstruction {
  const trimmedLine = line.trim();
  if (!trimmedLine) {
    return IGNORED_GANTT_LINE_INSTRUCTION;
  }

  const directiveInstruction = GANTT_DIRECTIVE_INSTRUCTION_PARSERS
    .map((parseInstruction) => parseInstruction(trimmedLine))
    .find(isGanttLineInstruction);
  if (directiveInstruction) {
    return directiveInstruction;
  }

  if (!trimmedLine.includes(TASK_METADATA_SEPARATOR)) {
    return IGNORED_GANTT_LINE_INSTRUCTION;
  }

  return {
    kind: GanttLineKind.Task,
    value: trimmedLine,
  };
}

function createInitialGanttParseState(): GanttParseState {
  return {
    title: null,
    dateFormat: GanttDateFormat.IsoDate,
    excludesWeekends: false,
    sections: [],
    taskDrafts: [],
    currentSectionIndex: -1,
    generatedTaskIndex: 0,
  };
}

function appendSection(input: {
  state: GanttParseState;
  label: string;
}): GanttParseState {
  const sectionIndex = input.state.sections.length;
  const section: GanttSection = {
    index: sectionIndex,
    label: input.label,
    taskIds: [],
  };

  return {
    ...input.state,
    sections: [...input.state.sections, section],
    currentSectionIndex: sectionIndex,
  };
}

function ensureCurrentSection(state: GanttParseState): GanttParseState {
  if (state.currentSectionIndex >= 0) {
    return state;
  }

  return appendSection({
    state,
    label: GANTT_DEFAULT_SECTION_LABEL,
  });
}

function appendTaskIdToSection(input: {
  section: GanttSection;
  taskId: string;
}): GanttSection {
  return {
    ...input.section,
    taskIds: [...input.section.taskIds, input.taskId],
  };
}

function appendTaskDraft(input: {
  state: GanttParseState;
  taskDraft: TaskDraft;
}): GanttParseState {
  const targetSection = input.state.sections[input.taskDraft.sectionIndex];
  if (!targetSection) {
    return input.state;
  }

  return {
    ...input.state,
    sections: input.state.sections.map((section) => (
      section.index === input.taskDraft.sectionIndex
        ? appendTaskIdToSection({ section, taskId: input.taskDraft.id })
        : section
    )),
    taskDrafts: [...input.state.taskDrafts, input.taskDraft],
  };
}

function applyIgnoredGanttLine(input: GanttLineReducerInput): GanttParseState {
  return input.state;
}

function applyTitleGanttLine(input: GanttLineReducerInput): GanttParseState {
  return {
    ...input.state,
    title: input.instruction.value ?? '',
  };
}

function applyDateFormatGanttLine(input: GanttLineReducerInput): GanttParseState {
  return {
    ...input.state,
    dateFormat: input.instruction.value ?? '',
  };
}

function applyExcludesWeekendsGanttLine(input: GanttLineReducerInput): GanttParseState {
  return {
    ...input.state,
    excludesWeekends: true,
  };
}

function applySectionGanttLine(input: GanttLineReducerInput): GanttParseState {
  return appendSection({
    state: input.state,
    label: input.instruction.value ?? '',
  });
}

function applyTaskGanttLine(input: GanttLineReducerInput): GanttParseState {
  const sectionState = ensureCurrentSection(input.state);
  const generatedId = `${GENERATED_TASK_ID_PREFIX}-${sectionState.currentSectionIndex}-${sectionState.generatedTaskIndex}`;
  const stateWithGeneratedId = {
    ...sectionState,
    generatedTaskIndex: sectionState.generatedTaskIndex + 1,
  };
  const parsedTaskDraft = parseTaskDraft({
    line: input.instruction.value ?? '',
    sectionIndex: sectionState.currentSectionIndex,
    generatedId,
    dateFormat: sectionState.dateFormat,
    excludesWeekends: sectionState.excludesWeekends,
  });

  if (!parsedTaskDraft) {
    return stateWithGeneratedId;
  }

  return appendTaskDraft({
    state: stateWithGeneratedId,
    taskDraft: parsedTaskDraft,
  });
}

const GANTT_LINE_REDUCER_BY_KIND: Readonly<Record<GanttLineKind, GanttLineReducer>> = {
  [GanttLineKind.DateFormat]: applyDateFormatGanttLine,
  [GanttLineKind.ExcludesWeekends]: applyExcludesWeekendsGanttLine,
  [GanttLineKind.Ignored]: applyIgnoredGanttLine,
  [GanttLineKind.Section]: applySectionGanttLine,
  [GanttLineKind.Task]: applyTaskGanttLine,
  [GanttLineKind.Title]: applyTitleGanttLine,
};

function applyGanttLine(input: {
  state: GanttParseState;
  line: string;
}): GanttParseState {
  const instruction = parseGanttLine(input.line);
  return GANTT_LINE_REDUCER_BY_KIND[instruction.kind]({
    state: input.state,
    instruction,
  });
}

function createTaskMap(tasks: GanttTask[]): Map<string, GanttTask> {
  return tasks.reduce((taskById, task) => {
    taskById.set(task.id, task);
    return taskById;
  }, new Map<string, GanttTask>());
}

function resolveGanttTitle(title: string | null): string | null {
  const normalizedTitle = (title ?? '').trim();
  return normalizedTitle.length > 0 ? normalizedTitle : null;
}

function hasParsedGanttDrafts(state: GanttParseState): boolean {
  return state.taskDrafts.length > 0 && state.sections.length > 0;
}

function hasResolvedTasks(tasks: GanttTask[] | null): tasks is GanttTask[] {
  return !!tasks && tasks.length > 0;
}

function createParsedGantt(state: GanttParseState): ParsedGantt | null {
  if (!hasParsedGanttDrafts(state)) {
    return null;
  }

  const tasks = resolveTaskDates({
    drafts: state.taskDrafts,
    excludesWeekends: state.excludesWeekends,
  });
  if (!hasResolvedTasks(tasks)) {
    return null;
  }

  const taskById = createTaskMap(tasks);

  return {
    title: resolveGanttTitle(state.title),
    dateFormat: state.dateFormat,
    excludesWeekends: state.excludesWeekends,
    sections: state.sections,
    tasks,
    taskById,
  };
}

export function parseMermaidGantt(raw: string): ParsedGantt | null {
  const lines = parseMermaidLines(raw);
  const headerIndex = findGanttHeaderIndex(lines);
  if (headerIndex < 0) {
    return null;
  }

  const parseState = lines.slice(headerIndex + 1).reduce<GanttParseState>(
    (state, line) => applyGanttLine({ state, line }),
    createInitialGanttParseState(),
  );

  return createParsedGantt(parseState);
}
